import React, { useState } from "react";
import "./styles/BusinessSheetTemplate.scss";

import EditDesignAndImgModal from "./EditDesignAndImgModal";
import {
  Card,
  CardTitle2,
  ChildCard,
  SectionTitle,
  CardData,
  LinksCard,
  SheetItemCard,
  StructuredCardTitle,
} from "./Cards";
import { normalizeSheetItems } from "../../utils/businessSheetUtils";
import CopySheetUrl from "./CopySheetUrl";
import GoldenSeriesAnalysis from "./GoldenSeriesAnalysis";
import PortfolioCard from "./PortfolioCard";

const BusinessSheetTemplate = ({
  data,
  isEdit,
  handleEdit,
  handleEditPortfolio,
  handleEditStructured,
  setBusinessSheetData,
}) => {
  // ポートフォリオは空配列を保つため JSON で保存する（未指定なら従来の handleEdit にフォールバック）
  const editPortfolio = handleEditPortfolio || handleEdit;
  // Issue #91: 構造化フィールド（金のシリーズ/パワーワード/アイテム）も
  // multipart でネストが化けないよう JSON 経路で保存する
  const editStructured = handleEditStructured || handleEdit;
  const [showGoldenAnalysis, setShowGoldenAnalysis] = useState(false);
  const getHeaderBackgroundSrc = (data) => {
    if (data?.headerBackgroundImageUrl) {
      return `${data?.headerBackgroundImageUrl}?timestamp=${new Date().getTime()}`;
    }

    if (data?.headerBackgroundImage) {
      return URL.createObjectURL(data?.headerBackgroundImage);
    }

    return "/images/headerBackgroundImage.png";
  };

  const getProfileImageSrc = (data) => {
    if (data?.profileImageUrl) {
      return `${data?.profileImageUrl}?timestamp=${new Date().getTime()}`;
    }

    if (data?.profileImage) {
      return URL.createObjectURL(data?.profileImage);
    }

    return "/images/profileImage.png";
  };

  const getReferralSheetBackgroundSrc = (data) => {
    if (data?.referralSheetBackgroundImageUrl) {
      return `${data?.referralSheetBackgroundImageUrl}?timestamp=${new Date().getTime()}`;
    }

    if (data?.referralSheetBackgroundImage) {
      return URL.createObjectURL(data?.referralSheetBackgroundImage);
    }

    return "/images/referralSheetBackgroundImage.png";
  };

  return (
    <div
      className="BusinessSheet"
      style={{
        fontFamily: data?.fontPreference && `${data?.fontPreference}`,
      }}
    >
      {/* header bg section */}
      <div className="headerBg">
        <img src={getHeaderBackgroundSrc(data)} alt="cover" />

        {isEdit && (
          <EditDesignAndImgModal
            size={"sm"}
            title={"プロフィール"}
            setBusinessSheetData={setBusinessSheetData}
            theme={data?.colorPreference}
            data={data}
          />
        )}
      </div>

      {/* profile section */}
      <div className="profile">
        <img src={getProfileImageSrc(data)} alt="profile" />

        <div className="profileContent">
          <div className="userData">
            <h6 className="userCategory">{data?.userCategory}</h6>
            <h1 className="userName">{data?.userName}</h1>
          </div>

          {isEdit && <CopySheetUrl userId={data?.userId} />}
        </div>
      </div>

      {/* main busniness info section */}
      <div className="businessSheetData-s1">
        <div className="left">
          {/* shortBiography */}
          <Card
            title={"メンバー略歴シート"}
            data={data?.shortBiography}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="shortBiography"
            placeholder={"メンバー略歴を入力"}
            maxLength={400}
          />

          {/* businessDescription */}
          <Card
            title={"ビジネスについて"}
            data={data?.businessDescription}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="businessDescription"
            placeholder={"ビジネスについて入力"}
            maxLength={400}
          />

          {/* personalInformation */}
          <Card
            title={"個人的な情報"}
            data={data?.personalInformation}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="personalInformation"
            placeholder={"個人的な情報について入力"}
            maxLength={1000}
          />

          {/* links */}
          <LinksCard
            theme={data?.colorPreference}
            isEdit={isEdit}
            data={data?.links}
            name="links"
            snsData={data?.snsLinks}
            handleEdit={handleEdit}
          />
        </div>

        <div className="right">
          <SectionTitle
            title={"ビジネスシート"}
            theme={data?.colorPreference}
          />

          {/* goals */}
          <ChildCard
            title={"目標"}
            data={data?.goals}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="goals"
            placeholder={"目標について入力"}
            maxLength={1000}
          />

          {/* accomplishments */}
          <ChildCard
            title={"実績"}
            data={data?.accomplishments}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="accomplishments"
            placeholder={"実績について入力"}
            maxLength={1000}
          />

          {/* achievements portfolio */}
          <PortfolioCard
            title={"実績一覧"}
            name="achievements"
            items={data?.achievements || []}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={editPortfolio}
          />

          {/* interests */}
          <ChildCard
            title={"興味・関心"}
            data={data?.interests}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="interests"
            placeholder={"興味・関心について入力"}
            maxLength={1000}
          />

          {/* networks */}
          <ChildCard
            title={"人脈"}
            data={data?.networks}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="networks"
            placeholder={"人脈について入力"}
            maxLength={1000}
          />

          {/* skills */}
          <ChildCard
            title={"能力"}
            data={data?.skills}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={handleEdit}
            name="skills"
            placeholder={"能力について入力"}
            maxLength={1000}
          />
        </div>
      </div>

      {/* background cover section */}
      <div className="businessSheetData-s2">
        <img src={getReferralSheetBackgroundSrc(data)} alt="background" />

        <div className="section2Container">
          <SectionTitle
            title={"リファーラルシート"}
            theme={data?.colorPreference}
          />

          {isEdit && (
            <button
              className="golden-series-ai-btn"
              onClick={() => setShowGoldenAnalysis(true)}
            >
              AIで再分析
            </button>
          )}

          {showGoldenAnalysis && (
            <div className="golden-series-modal-overlay">
              <div className="golden-series-modal">
                <div className="golden-series-modal-header">
                  <span>金のシリーズ AI分析</span>
                  <button
                    className="golden-series-modal-close"
                    onClick={() => setShowGoldenAnalysis(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="golden-series-modal-body">
                  <GoldenSeriesAnalysis
                    onConfirm={(result) => {
                      handleEdit(result);
                      setShowGoldenAnalysis(false);
                    }}
                    initialValues={{
                      goldenEgg: data?.goldenEgg,
                      goldenGoose: data?.goldenGoose,
                      goldenFarmer: data?.goldenFarmer,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* goldenEgg / goldenGoose / goldenFarmer（Issue #91: 番号 + 3分割） */}
          {[
            { name: "goldenEgg", title: "金のタマゴ" },
            { name: "goldenGoose", title: "金のガチョウ" },
            { name: "goldenFarmer", title: "金のファーマー" },
          ].map(({ name, title }) => (
            <div className={name} key={name}>
              <StructuredCardTitle
                title={title}
                theme={data?.colorPreference}
                name={name}
                kind="goldenSeries"
                isEdit={isEdit}
                data={data?.[name]}
                handleEdit={editStructured}
              />
              {normalizeSheetItems(data?.[name], "goldenSeries").map(
                (item, index) => (
                  <SheetItemCard
                    key={index}
                    kind="goldenSeries"
                    rank={index + 1}
                    item={item}
                  />
                ),
              )}
            </div>
          ))}

          {/* companyStrengths */}
          <div className="companyStrengths">
            <CardTitle2
              title={"自社の強み"}
              theme={data?.colorPreference}
              name="companyStrengths"
              isEdit={isEdit}
              data={data?.companyStrengths}
              handleEdit={handleEdit}
              placeholder={"自社の強みについて入力"}
              maxLength={1000}
            />
            <CardData data={data?.companyStrengths} />
          </div>

          {/* powerWords（Issue #91: 上下2分割 × 6件） */}
          <div className="powerWords">
            <StructuredCardTitle
              title={"パワーワード"}
              theme={data?.colorPreference}
              name="powerWords"
              kind="powerWord"
              isEdit={isEdit}
              data={data?.powerWords}
              handleEdit={editStructured}
            />
            <div className="gridContainer">
              {normalizeSheetItems(data?.powerWords, "powerWord").map(
                (item, index) => (
                  <SheetItemCard key={index} kind="powerWord" item={item} />
                ),
              )}
            </div>
          </div>

          {/* products（Issue #91: タイトル / URL / 内容 の3分割 × 3件） */}
          <div className="products">
            <StructuredCardTitle
              title={"アイテム / 商品・商材"}
              theme={data?.colorPreference}
              name="itemsProducts"
              kind="itemProduct"
              isEdit={isEdit}
              data={data?.itemsProducts}
              handleEdit={editStructured}
            />
            {normalizeSheetItems(data?.itemsProducts, "itemProduct").map(
              (item, index) => (
                <SheetItemCard key={index} kind="itemProduct" item={item} />
              ),
            )}
          </div>

          {/* services portfolio */}
          <PortfolioCard
            title={"サービス一覧"}
            name="services"
            items={data?.services || []}
            theme={data?.colorPreference}
            isEdit={isEdit}
            handleEdit={editPortfolio}
          />
        </div>
      </div>
    </div>
  );
};

export default BusinessSheetTemplate;
